import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Link as RouterLink } from "react-router-dom";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";


type FaqItem = {
  question: string;
  answer: string;
};

type FundingItem = {
  id: string;
  cardTitle: string;
  detailTitle: string;
  body: string;
};

const REGION_COVERAGE = "Heilbronn, Stuttgart, Karlsruhe, Ludwigsburg und Umgebung";





export default function HaeufigeFragen() {
  const { t } = useTranslation();
  const rawFunding = (t("faqPage.fundingItems", { returnObjects: true }) as any) || [];
  const ids = ["feedin", "regional", "tax", "finance"];
  const FUNDING_ITEMS: FundingItem[] = rawFunding.map((item: any, i: number) => ({ ...item, id: ids[i] }));
  const FAQ_ITEMS: FaqItem[] = (t("faqPage.faqItems", { returnObjects: true }) as any) || [];

  const [activeFundingId, setActiveFundingId] = useState<string>(FUNDING_ITEMS[0].id);
  const activeFunding = useMemo(
    () => FUNDING_ITEMS.find((item) => item.id === activeFundingId) ?? FUNDING_ITEMS[0],
    [activeFundingId]
  );

  return (
    <Container sx={{ py: { xs: 4, md: 6 } }}>
      <Box className="hero-shell" sx={{ p: { xs: 4, md: 6 }, position: "relative", mb: { xs: 4, md: 6 } }}>
        <Box className="hero-glow" />
        <Stack spacing={1.5} sx={{ position: "relative", zIndex: 1, maxWidth: 860 }}>
          <Typography variant="h1">Haeufig gestellte Fragen</Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Antworten zu Kosten, Foerderung, Genehmigung, Speicher und Wirtschaftlichkeit Ihrer
            Photovoltaikanlage in {REGION_COVERAGE}.
          </Typography>
        </Stack>
      </Box>

      <Box
        sx={{
          mb: { xs: 4, md: 6 },
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", lg: "minmax(320px, 430px) minmax(0, 1fr)" },
          alignItems: "start",
        }}
      >
        <Stack spacing={2.2}>
          <Stack spacing={1.2}>
            <Typography className="section-label">{t("faqPage.sectionLabel")}</Typography>
            <Typography variant="h2">{t("faqPage.title")}</Typography>
            <Typography variant="body1" color="text.secondary">
              {t("faqPage.subtitle")}
            </Typography>
          </Stack>

          <Stack spacing={{ xs: 1.25, md: 1.5 }}>
            {FUNDING_ITEMS.map((item) => (
              <Paper
                key={item.id}
                elevation={0}
                component="button"
                type="button"
                onClick={() => setActiveFundingId(item.id)}
                aria-pressed={activeFundingId === item.id}
                sx={{
                  minHeight: { xs: 86, md: 110 },
                  px: { xs: 2.5, md: 3.2 },
                  borderRadius: 3.2,
                  border:
                    activeFundingId === item.id
                      ? "1px solid color-mix(in srgb, var(--accent) 70%, var(--border))"
                      : "1px solid var(--border)",
                  background:
                    activeFundingId === item.id
                      ? "linear-gradient(140deg, color-mix(in srgb, var(--accent) 20%, var(--surface)) 0%, var(--surface) 100%)"
                      : "var(--surface)",
                  textAlign: "center",
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                  transition: "all 0.22s ease",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow:
                    activeFundingId === item.id
                      ? "0 16px 30px -24px color-mix(in srgb, var(--accent) 85%, transparent)"
                      : "none",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 16,
                    bottom: 16,
                    width: 5,
                    borderRadius: "0 10px 10px 0",
                    background:
                      activeFundingId === item.id
                        ? "linear-gradient(180deg, var(--accent-warm), var(--accent))"
                        : "transparent",
                    transition: "all 0.22s ease",
                  },
                  "&:hover": {
                    borderColor: "var(--accent)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: "1.05rem", md: "1.25rem" },
                    lineHeight: 1.1,
                    whiteSpace: "pre-line",
                    color: activeFundingId === item.id ? "var(--accent)" : "inherit",
                  }}
                >
                  {item.cardTitle}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            border: "1px solid var(--border)",
            borderRadius: 4,
            minHeight: { lg: 560 },
            background:
              "linear-gradient(180deg, color-mix(in srgb, var(--surface) 92%, var(--accent) 8%) 0%, var(--surface) 100%)",
            "@keyframes faqFadeIn": {
              "0%": { opacity: 0, transform: "translateY(8px)" },
              "100%": { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Box key={activeFunding.id} sx={{ animation: "faqFadeIn 220ms ease" }}>
            <Stack spacing={2.2}>
              <Typography className="section-label">{t("faqPage.detailSectionLabel")}</Typography>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.7rem", md: "2.2rem" } }}>
                {activeFunding.detailTitle}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.68, maxWidth: 880 }}>
                {activeFunding.body}
              </Typography>
            </Stack>
          </Box>
        </Paper>
      </Box>

      <Paper
        elevation={0}
        sx={{
          mb: { xs: 4, md: 6 },
          p: { xs: 2.4, md: 3 },
          borderRadius: 3,
          border: "1px solid var(--border)",
          background: "color-mix(in srgb, var(--surface) 80%, var(--accent-cool) 20%)",
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.75 }}>
          {t("faqPage.regionServiceTitle", { region: REGION_COVERAGE })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
          {t("faqPage.regionServiceBody")}
        </Typography>
      </Paper>

      <Stack spacing={1} sx={{ mb: 2 }}>
        <Typography className="section-label">{t("faqPage.faqSectionLabel")}</Typography>
        <Typography variant="h2">{t("faqPage.faqTitle")}</Typography>
      </Stack>

      <Stack spacing={1.5}>
        {FAQ_ITEMS.map((item) => (
          <Accordion
            key={item.question}
            disableGutters
            elevation={0}
            sx={{
              border: "1px solid var(--border)",
              borderRadius: "16px !important",
              background: "var(--surface)",
              overflow: "hidden",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 2.5, py: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {item.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
              <Typography variant="body2" color="text.secondary">
                {item.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          mt: { xs: 4, md: 5 },
          p: { xs: 3, md: 4 },
          border: "1px solid var(--border)",
          background: "var(--surface)",
          textAlign: { xs: "left", md: "center" },
        }}
      >
        <Typography variant="h2" sx={{ mb: 1.5 }}>
          {t("faqPage.ctaTitle")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5 }}>
          {t("faqPage.ctaBody", { region: REGION_COVERAGE })}
        </Typography>
        <Button variant="contained" color="secondary" component={RouterLink} to="/kontakt">
          {t("faqPage.ctaButton")}
        </Button>
      </Paper>
    </Container>
  );
}
